import { Clock, ArrowRight, SkipForward, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useBrand } from '@/hooks/useBrand'

const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
]

// Wizard exposes English + Spanish only for now. The other languages
// (Mandarin, Vietnamese, etc.) used to be in the dropdown but every non-
// English firm we've onboarded ended up needing custom prompt + STT/TTS
// tuning that the wizard doesn't apply, so we'd ship a broken-feeling
// agent. Spanish itself flows through a different stack (multilingual STT
// + Rime ArcanaV3 voice, see VoiceEngineHelpers.tsx PRESETS) and isn't
// safe to flip on automatically — it's behind a "talk to support" gate
// below until that gets wired into the wizard properly.
const LANGUAGE_OPTIONS = ['English', 'Spanish'] as const
const SUPPORT_REQUIRED_LANGUAGES = new Set<string>(['Spanish'])

interface BuilderPhaseOperationsProps {
  firmTimezone: string
  businessHours: string
  officePhone: string
  primaryLanguage: string
  additionalLanguages: string[]
  afterHoursMessage: string
  bookingUrl: string
  onSetTimezone: (tz: string) => void
  onSetBusinessHours: (hours: string) => void
  onSetOfficePhone: (phone: string) => void
  onSetPrimaryLanguage: (lang: string) => void
  onSetAdditionalLanguages: (langs: string[]) => void
  onSetAfterHoursMessage: (msg: string) => void
  onSetBookingUrl: (url: string) => void
  onContinue: () => void
  onSkip: () => void
}

export default function BuilderPhaseOperations({
  firmTimezone,
  businessHours,
  officePhone,
  primaryLanguage,
  additionalLanguages,
  afterHoursMessage,
  bookingUrl,
  onSetTimezone,
  onSetBusinessHours,
  onSetOfficePhone,
  onSetPrimaryLanguage,
  onSetAdditionalLanguages,
  onSetAfterHoursMessage,
  onSetBookingUrl,
  onContinue,
  onSkip,
}: BuilderPhaseOperationsProps) {
  // Additional-language picker temporarily removed — Spanish-speaking
  // experience needs more polish before we expose multi-language as a
  // wizard option. State plumbing left in place so existing agents keep
  // their saved languages and we can re-enable the UI cleanly later.
  // The `additionalLanguages` and `onSetAdditionalLanguages` props are
  // intentionally unused below.
  void additionalLanguages
  void onSetAdditionalLanguages

  const brand = useBrand()
  const supportEmail = brand.supportEmail ?? 'support@legallady.ai'

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-brand-royal" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Office operations
          </p>
        </div>
        <button
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          onClick={onSkip}
        >
          <SkipForward className="h-3 w-3" />
          Skip
        </button>
      </div>

      <p className="text-sm text-muted-foreground">
        Help your AI know when you're open and how to handle callers outside business hours. All fields are optional.
      </p>

      <div className="space-y-4">
        {/* Timezone */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Timezone</p>
          <Select value={firmTimezone} onValueChange={onSetTimezone}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {COMMON_TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Business Hours */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Business hours</p>
          <Input
            placeholder="e.g., Monday through Friday, 9am to 5pm"
            value={businessHours}
            onChange={(e) => onSetBusinessHours(e.target.value)}
            className="text-sm"
          />
        </div>

        {/* Office Phone */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Transfer number <span className="text-muted-foreground font-normal">(live call transfer)</span></p>
          <Input
            placeholder="e.g., (212) 555-1234"
            value={officePhone}
            onChange={(e) => onSetOfficePhone(e.target.value)}
            className="text-sm"
          />
          <p className="text-[11px] text-muted-foreground">
            When a caller requests a live attorney, the AI will transfer to this number. Outbound follow-up calls use the number assigned to your agent on the AI Agents page.
          </p>
        </div>

        {/* Booking URL */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Online booking link <span className="text-muted-foreground font-normal">(Calendly, etc.)</span></p>
          <Input
            placeholder="e.g., https://calendly.com/yourfirm"
            value={bookingUrl}
            onChange={(e) => onSetBookingUrl(e.target.value)}
            className="text-sm"
          />
        </div>

        {/* Primary Language */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Primary language</p>
          <Select value={primaryLanguage} onValueChange={onSetPrimaryLanguage}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select primary language" />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map(lang => (
                <SelectItem key={lang} value={lang}>{lang}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {SUPPORT_REQUIRED_LANGUAGES.has(primaryLanguage) && (
            <div
              className="mt-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2.5 text-[12px] leading-relaxed text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
              role="note"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Spanish set-up runs through our team.</p>
                  <p className="mt-1">
                    Spanish agents use a different voice + transcription stack and need a manual handoff to dial in the prompt for native callers.
                    Continue with this wizard to spin up an English agent first — then email{' '}
                    <a href={`mailto:${supportEmail}`} className="underline font-medium">{supportEmail}</a>{' '}
                    and we'll convert the agent to Spanish (usually same day).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* After-Hours Message */}
        <div className="space-y-2">
          <p className="text-sm font-medium">After-hours instructions <span className="text-muted-foreground font-normal">(optional)</span></p>
          <Textarea
            placeholder="e.g., No live transfers after 7pm or before 7am — take a message and we'll call back first thing in the morning. For emergencies, tell callers to call 911."
            value={afterHoursMessage}
            onChange={(e) => onSetAfterHoursMessage(e.target.value)}
            className="min-h-[72px] text-sm resize-none"
          />
          <p className="text-[11px] text-muted-foreground/80">
            Goes straight into your AI's instructions. The AI knows the current time, so rules like "no transfers after 7pm" are honored automatically.
          </p>
        </div>
      </div>

      <Button className="w-full" onClick={onContinue}>
        Continue
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
  )
}
